import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';
import { cn } from '@/lib/utils';

/**
 * Empty — estados vacíos del panel (registry @coss/empty).
 * Adaptado a Tailwind v3.4: se reemplazaron utilidades de v4
 * (shadow-sm/5, --theme(), not-dark:, size-4.5, scale-84, font-heading,
 * --radius-md) por equivalentes v3 + tokens del DS.
 *
 * Composición: <Empty><EmptyHeader><EmptyMedia variant="icon"><Icon/>
 * </EmptyMedia><EmptyTitle/><EmptyDescription/></EmptyHeader>
 * <EmptyContent>…acciones…</EmptyContent></Empty>
 */
const emptyMediaVariants = cva(
  "flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-6",
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default: 'bg-transparent text-muted-foreground',
        icon: 'relative flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground shadow-sm',
      },
    },
  }
);

export function Empty({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center gap-6 px-6 py-12 text-center md:py-16',
        className
      )}
      data-slot="empty"
      {...props}
    />
  );
}

export function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className={cn('flex max-w-sm flex-col items-center gap-1 text-center', className)}
      data-slot="empty-header"
      {...props}
    />
  );
}

export function EmptyMedia({
  className,
  variant = 'default',
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>): React.ReactElement {
  return (
    <div className={cn('relative mb-2', className)} data-slot="empty-media" data-variant={variant}>
      {variant === 'icon' && (
        <>
          <div
            aria-hidden="true"
            className={cn(
              emptyMediaVariants({ variant }),
              'pointer-events-none absolute bottom-0 origin-bottom-left -translate-x-1 -rotate-[10deg] scale-90 opacity-60 shadow-none'
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              emptyMediaVariants({ variant }),
              'pointer-events-none absolute bottom-0 origin-bottom-right translate-x-1 rotate-[10deg] scale-90 opacity-60 shadow-none'
            )}
          />
        </>
      )}
      <div className={cn(emptyMediaVariants({ variant }))} {...props}>
        {children}
      </div>
    </div>
  );
}

export function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className={cn('text-lg font-semibold text-foreground', className)}
      data-slot="empty-title"
      {...props}
    />
  );
}

export function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>): React.ReactElement {
  return (
    <p
      className={cn(
        'text-sm text-muted-foreground [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className
      )}
      data-slot="empty-description"
      {...props}
    />
  );
}

export function EmptyContent({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className={cn(
        'flex w-full min-w-0 max-w-sm flex-col items-center gap-3 text-sm',
        className
      )}
      data-slot="empty-content"
      {...props}
    />
  );
}
